import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ExternalLink, Copy } from 'lucide-react';

const TwilioConfigGuide = ({ error, onClose }) => {
  const [showCredentials, setShowCredentials] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard?.writeText(text)?.then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const isWhatsAppSandboxError = error?.code === 'WHATSAPP_SANDBOX_ERROR';
  const isConfigError = error?.code === 'TWILIO_CONFIG_ERROR';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isWhatsAppSandboxError ? 'WhatsApp Sandbox Setup Required' : 'Twilio Configuration Required'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {isWhatsAppSandboxError 
                    ? 'Your phone number needs verification in Twilio WhatsApp Sandbox'
                    : 'Set up Twilio WhatsApp integration to send OTP messages'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* WhatsApp Sandbox Urgent Action */}
          {isWhatsAppSandboxError && error?.configurationHelp?.urgentAction && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-900 mb-2">
                    {error?.configurationHelp?.urgentAction?.title}
                  </h3>
                  <p className="text-red-700 text-sm mb-3">
                    {error?.configurationHelp?.urgentAction?.message}
                  </p>
                  <div className="bg-white rounded border border-red-200 p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">WhatsApp Sandbox Number</span>
                      <button
                        onClick={() => copyToClipboard('+1 415 523 8886', 'sandbox')}
                        className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        {copiedField === 'sandbox' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div className="text-lg font-mono bg-gray-50 p-2 rounded">+1 415 523 8886</div>
                  </div>
                  <ol className="text-sm text-red-700 space-y-1 list-decimal list-inside">
                    {error?.configurationHelp?.urgentAction?.steps?.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                  {error?.configurationHelp?.urgentAction?.note && (
                    <p className="text-xs text-red-600 mt-3 font-medium">
                      ℹ️ {error?.configurationHelp?.urgentAction?.note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Environment Variables Configuration */}
          {isConfigError && error?.configurationHelp && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Required Environment Variables</h3>
                <p className="text-blue-700 text-sm mb-3">
                  Add these variables to your <code className="bg-blue-100 px-1 rounded">.env</code> file:
                </p>
                
                <div className="space-y-3">
                  {error?.configurationHelp?.variables?.map((variable, index) => {
                    const [key, description] = variable?.split(' - ');
                    return (
                      <div key={index} className="bg-white rounded border border-blue-200 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <code className="text-sm font-mono text-gray-900">{key}</code>
                          <button
                            onClick={() => copyToClipboard(key, key)}
                            className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            {copiedField === key ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                        <p className="text-xs text-gray-600">{description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Setup Instructions */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-medium text-green-900 mb-2 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Setup Instructions
                </h3>
                <ol className="text-sm text-green-700 space-y-2 list-decimal list-inside">
                  {error?.configurationHelp?.instructions?.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ol>
              </div>

              {/* Troubleshooting */}
              {error?.configurationHelp?.troubleshooting && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-medium text-amber-900 mb-3">
                    {error?.configurationHelp?.troubleshooting?.title}
                  </h3>
                  <div className="space-y-3">
                    {error?.configurationHelp?.troubleshooting?.issues?.map((issue, index) => (
                      <div key={index} className="border-l-2 border-amber-300 pl-3">
                        <p className="text-sm font-medium text-amber-900">{issue?.problem}</p>
                        <p className="text-sm text-amber-700">{issue?.solution}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick Links */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Links</h3>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://www.twilio.com/console"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Twilio Console
              </a>
              <a
                href="https://www.twilio.com/console/sms/whatsapp/sandbox"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                WhatsApp Sandbox
              </a>
              <a
                href="https://www.twilio.com/docs/whatsapp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Documentation
              </a>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
            <a
              href="https://www.twilio.com/console"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors inline-flex items-center"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Twilio Console
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwilioConfigGuide;